/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*
* Create a bar chart to display above a table
* Yes - OK - It is a column chart really, but never heard anyone in real world call it a column chart...
*/
import { AbstractChart, ChartPoint, compareKeys } from "@src/app/shared/chart/charts/abstract-chart";

type CategoryKey = string | number;

export class TrendLine extends AbstractChart {

    height = 200;

    /* Loosely typed so a subclass can return a differently shaped options bag - see CompareBar */
    getTooltip(): object {
        const format = this.formatMetric.bind(this);

        return {
            shared: true,
            headerFormat: '<span style="font-size:12px"><b>{point.key}</b></span><br>',
            pointFormatter(this: ChartPoint) {
                return `${this.series.name}: <b>${format(this.y)}</b><br/>`;
            }
        };
    }

    getChartTitle() {
        return { text: this.trendField.label, style: { fontSize: '14px', fontWeight: 'bold' } };
    }

    /** What the x-axis is labelled with, and what the totals are keyed and ordered by */
    categories: string[] = [];
    protected categoryKeys: CategoryKey[] = [];

    protected createChartOptions(): object {

        const options = {
            chart: {
                backgroundColor: this.chartBackgroundColor,
                borderRadius: this.chartBorderRadius,
                plotBorderWidth: null,
                type: 'area',
                height: this.height,
            },
            title: this.getChartTitle(),
            credits: { enabled: false },
            tooltip: this.getTooltip(),
            xAxis: {
                categories: this.categories,
            },
            yAxis: {
                title: { text: (this.numberField ? this.numberField.label : 'Count') },
            },
            //legend: {enabled: false},
            plotOptions: {
                area: {
                    stacking: 'normal',
                    lineColor: '#666666',
                    lineWidth: 1,
                    marker: {
                        enabled: false,
                        lineWidth: 1,
                        lineColor: '#666666'
                    }
                },
            },
            series: this.series
        };
        return options;
    }

    protected analyseData() {
        const mapper = new Map<CategoryKey, Map<number, { total: number }>>();

        const plMapper = new Map<number, string>();

        //let grandTotal = 0;
        //const type = this.trendField.label;

        this.series = [];
        this.categoryKeys = [];

        this.data.map(o => {
            const trendValue = this.trendField.getValue(o);
            const pickValue = this.picklist.getValue(o);

            if (trendValue) {
                if (!mapper.has(trendValue)) {
                    mapper.set(trendValue, new Map())
                    this.categoryKeys.push(trendValue);
                }
                const oTrendMap = mapper.get(trendValue);

                if (!plMapper.has(pickValue)) {
                    plMapper.set(pickValue, this.picklist.getDisplayValueFromId(pickValue));
                }

                if (!oTrendMap.has(pickValue)) {
                    oTrendMap.set(pickValue, { total: 0 });
                }
                const oPickMap = oTrendMap.get(pickValue);

                const tValue = (this.numberField ? this.numberField.getValue(o) : 1);
                oPickMap.total = oPickMap.total + tValue;
                //grandTotal += tValue;
            }
        });

        /*
         * Key and label are two different things: a picklist trend keys on its id so months order
         * 1..12, and formatValue turns that id into the name shown under the bar. A plain
         * TrendField has no lookup, so formatValue hands the key straight back.
         */
        this.categoryKeys = this.categoryKeys.sort(compareKeys);
        this.categories = this.categoryKeys.map(k => this.trendField.formatValue(k));

        for (const k of plMapper.keys()) {
            this.series.push({ name: plMapper.get(k), data: this.getSeriesData(mapper, k) })
        }
    }

    private getSeriesData(mapper: Map<CategoryKey, Map<number, { total: number }>>, k: number) {
        const data = [];
        for (const c of this.categoryKeys) {
            const catEntry = mapper.get(c).get(k);
            if (catEntry) {
                data.push(catEntry.total);
            } else {
                data.push(0);
            }

        }
        return data;
    }
}
